<script>
    import { readState, writeState } from './urlState.js';

    export let collection;
    export let entries = [];

    // The facet the server already filtered this page by, when the page is a facet landing page.
    // Its URL carries the facet in the path rather than the query string, so there is nothing for
    // readState to find and the browser would otherwise hydrate showing everything.
    export let initialFacet = null;

    let query = '';
    let view = 'grid';
    let grouping = collection.groupings[0].value;
    let sort = collection.sorts[0].value;
    let facetValues = {};
    let searchInput;
    let resultsTop;

    const defaults = {
        q: '',
        view: 'grid',
        group: collection.groupings[0].value,
        sort: collection.sorts[0].value,
    };

    for (const facet of collection.facets) {
        facetValues[facet.id] = 'all';
        defaults[facet.id] = 'all';
    }

    // Seeded as the default as well as the value, so writeState treats it as "nothing to say" and
    // leaves the clean facet URL alone until the reader actually changes a filter.
    if (initialFacet && initialFacet.id in defaults) {
        facetValues[initialFacet.id] = initialFacet.value;
        defaults[initialFacet.id] = initialFacet.value;
    }

    // Restored during init rather than in onMount: the reactive block below writes the
    // state back to the URL, and reactive statements run before onMount would have read
    // it, so a linked filter would be erased before it was ever applied. Reading here
    // also avoids rendering an unfiltered list for one frame.
    restoreFromUrl();

    function restoreFromUrl() {
        const state = readState(Object.keys(defaults));
        if (state.q) query = state.q;
        if (state.view === 'grid' || state.view === 'list') view = state.view;
        if (state.group && collection.groupings.some((option) => option.value === state.group))
            grouping = state.group;
        if (state.sort && collection.sorts.some((option) => option.value === state.sort))
            sort = state.sort;

        // Facet values are validated the same way group and sort are: a stale link such
        // as ?tag=Old%20Tag would otherwise yield an unexplained empty result and a
        // select rendering blank, with no way to tell which control caused it.
        const restored = { ...facetValues };
        for (const facet of collection.facets) {
            const value = state[facet.id];
            if (value && facet.options(entries).some((option) => option.value === value)) {
                restored[facet.id] = value;
            }
        }
        facetValues = restored;
    }

    $: normalisedQuery = query.trim().toLowerCase();

    $: activeGrouping =
        collection.groupings.find((option) => option.value === grouping) || collection.groupings[0];

    $: activeSort = collection.sorts.find((option) => option.value === sort) || collection.sorts[0];

    // A section only earns the accent when the grouping is the collection's identity axis, so
    // grouping roles by tag no longer stamps faction-<tag> on every heading. The colour comes
    // from the class rather than an inline style, so it works for every collection that
    // declares one instead of only for roles.
    $: showAccent = collection.accentGrouping && activeGrouping.value === collection.accentGrouping;

    // Takes accent as an argument rather than closing over showAccent, for the same reason
    // matchesQuery and matchesFacetsExcept below take their reactive inputs explicitly: a call
    // written as {accentClassFor(group.key)} only tracks `group`, so it would not redraw the
    // class on a showAccent change that happened without also changing the group list.
    function accentClassFor(key, accent) {
        return accent ? `${collection.accentPrefix}${key}` : '';
    }

    // These take the query and the facet values as arguments rather than closing over
    // them. Svelte's reactive statements only track identifiers that appear in the
    // statement itself, so a helper that closed over facetValues would leave `filtered`
    // stale: the list would stop responding to the filters entirely.
    // Every whitespace-separated term must appear somewhere in the entry's search text.
    // A single includes() against the concatenation would only match terms that happen to
    // be adjacent in the order searchText joins them, so "village seer" found nothing.
    function matchesQuery(entry, needle) {
        if (!needle) return true;

        const haystack = collection.searchText(entry).toLowerCase();
        return needle.split(/\s+/).every((term) => haystack.includes(term));
    }

    function matchesFacetsExcept(entry, values, exceptId) {
        for (const facet of collection.facets) {
            if (facet.id === exceptId) continue;

            const value = values[facet.id];
            if (value && value !== 'all' && !facet.matches(entry, value)) return false;
        }

        return true;
    }

    // Leave-one-out: each select offers the values that are still reachable given every
    // other active facet and the search box, so no option can be picked that yields an
    // empty list. The currently selected value is always kept, otherwise removing it
    // from its own select would render that control blank.
    $: facetsWithOptions = collection.facets.map((facet) => {
        const reachable = entries.filter(
            (entry) =>
                matchesQuery(entry, normalisedQuery) &&
                matchesFacetsExcept(entry, facetValues, facet.id),
        );
        const selected = facetValues[facet.id];

        return {
            ...facet,
            resolvedOptions: facet
                .options(reachable)
                .filter(
                    (option) =>
                        option.value === selected ||
                        reachable.some((entry) => facet.matches(entry, option.value)),
                ),
        };
    });

    $: hasActiveFilter =
        normalisedQuery !== '' || Object.keys(facetValues).some((id) => facetValues[id] !== 'all');

    $: activeFilters = buildActiveFilters(query, normalisedQuery, facetValues, facetsWithOptions);

    $: filtered = entries
        .filter(
            (entry) =>
                matchesQuery(entry, normalisedQuery) &&
                matchesFacetsExcept(entry, facetValues, null),
        )
        .slice()
        .sort(activeSort.compare);

    $: groups = buildGroups(filtered, activeGrouping);

    $: writeState(
        {
            q: query,
            view,
            group: grouping,
            sort,
            ...facetValues,
        },
        defaults,
    );

    // An entry whose keysOf returns nothing still has to appear somewhere: dropping it
    // would contradict the "showing N of M" line, which counts the filtered list rather
    // than the grouped one. Keyless entries collect into a trailing bucket instead.
    const keylessGroupKey = '__keyless';

    // A key absent from the hardcoded order would otherwise sort ahead of every listed
    // group, because indexOf returns -1. Group orders are literals while the keys come
    // from hand-edited JSON, so an unlisted key is a question of when, not if.
    function orderIndex(order, key) {
        const index = order.indexOf(key);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    }

    function buildGroups(list, group) {
        if (!group || group.value === 'none') {
            return [{ key: 'all', label: '', entries: list }];
        }

        const buckets = new Map();
        const keyless = [];
        for (const entry of list) {
            const keys = group.keysOf(entry) || [];
            if (keys.length === 0) {
                keyless.push(entry);
                continue;
            }

            for (const key of keys) {
                if (!buckets.has(key)) {
                    buckets.set(key, { key, label: group.labelOf(entry, key), entries: [] });
                }
                buckets.get(key).entries.push(entry);
            }
        }

        const ordered = [...buckets.values()];
        if (group.order) {
            ordered.sort(
                (left, right) =>
                    orderIndex(group.order, left.key) - orderIndex(group.order, right.key),
            );
        } else {
            ordered.sort((left, right) =>
                left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
            );
        }

        if (keyless.length > 0) {
            ordered.push({
                key: keylessGroupKey,
                label: group.emptyLabel || 'Not recorded',
                entries: keyless,
            });
        }

        return ordered;
    }

    // One chip per non-default facet, plus the search box, so a reader who has narrowed
    // six facets can drop one of them instead of starting over with Clear filters.
    const searchFilterId = '__search';

    function buildActiveFilters(rawQuery, needle, values, facets) {
        const active = [];

        if (needle !== '') {
            active.push({ id: searchFilterId, label: 'Search', display: rawQuery.trim() });
        }

        for (const facet of facets) {
            const value = values[facet.id];
            if (!value || value === 'all') continue;

            const option = facet.resolvedOptions.find((candidate) => candidate.value === value);
            active.push({
                id: facet.id,
                label: facet.label,
                display: option ? option.label : value,
            });
        }

        return active;
    }

    // A role appears under every tag it carries, so the group counts can add up past the
    // "showing N" line. Stating that is cheaper than either hiding the counts or
    // arbitrarily picking one group per entry.
    $: groupedEntryCount = groups.reduce((total, group) => total + group.entries.length, 0);

    $: groupsRepeatEntries = groupedEntryCount > filtered.length;

    // Collapsed state is a user choice, so it lives here; whether the toggle is reachable
    // at all is a layout question and stays in a CSS media query.
    let filtersCollapsed = true;

    function clearFilters() {
        query = '';
        const cleared = {};
        for (const facet of collection.facets) cleared[facet.id] = 'all';
        facetValues = cleared;
    }

    function clearFilter(id) {
        if (id === searchFilterId) {
            query = '';
            return;
        }

        selectFacet(id, 'all');
    }

    // Guarding on document.body would kill the shortcut for the rest of the session
    // after any click, because focus moves to the clicked pill or select and stays
    // there. Bailing out of text entry contexts is the behaviour actually wanted.
    const textEntrySelector = 'input, textarea, select, [contenteditable]';

    function handleWindowKeydown(event) {
        if (event.key !== '/') return;
        if (event.target && event.target.closest && event.target.closest(textEntrySelector)) return;

        event.preventDefault();
        if (searchInput) searchInput.focus();
    }

    function handleSearchKeydown(event) {
        if (event.key === 'Escape') {
            query = '';
        }
    }

    function selectFacet(id, value) {
        facetValues = { ...facetValues, [id]: value };
    }

    function prefersReducedMotion() {
        return (
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
    }

    function scrollTo(element) {
        if (!element || !element.scrollIntoView) return;

        element.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start',
        });
        if (element.focus) element.focus({ preventScroll: true });
    }

    // A pill clicked far down the list collapses the results above the viewport, so
    // without this the page looks unchanged and the filter looks broken.
    function selectFacetFromCard(event) {
        selectFacet(event.detail.id, event.detail.value);
        scrollTo(resultsTop);
    }

    // Group headings stop being visible once scrolled past, and the roles catalogue
    // groups 137 entries into 11 factions by default, so without these the only way to
    // reach the last group is to scroll through every group above it.
    const groupHeadings = {};

    function jumpToGroup(key) {
        scrollTo(groupHeadings[key]);
    }

    $: showGroupJumpLinks = groups.length > 1 && groups.some((group) => group.label);

    $: facetFilterCount = activeFilters.filter((filter) => filter.id !== searchFilterId).length;
</script>

<svelte:window on:keydown={handleWindowKeydown} />

<div class="guide-browser-app">
    <div class="guide-controls">
        <label class="guide-search">
            <span class="sr-only">{collection.searchLabel}</span>
            <i class="fa fa-search" aria-hidden="true"></i>
            <input
                type="search"
                placeholder={collection.searchPlaceholder}
                bind:value={query}
                bind:this={searchInput}
                on:keydown={handleSearchKeydown}
                aria-label={collection.searchLabel}
            />
        </label>

        <div class="guide-view-toggle" role="group" aria-label="View style">
            <button
                type="button"
                class="guide-view-btn"
                class:active={view === 'grid'}
                aria-pressed={view === 'grid'}
                on:click={() => (view = 'grid')}
            >
                <i class="fa fa-th-large" aria-hidden="true"></i> Summary
            </button>
            <button
                type="button"
                class="guide-view-btn"
                class:active={view === 'list'}
                aria-pressed={view === 'list'}
                on:click={() => (view = 'list')}
            >
                <i class="fa fa-list" aria-hidden="true"></i> Detail
            </button>
        </div>
    </div>

    <button
        type="button"
        class="guide-filter-toggle"
        aria-expanded={!filtersCollapsed}
        aria-controls="guide-prop-filter"
        on:click={() => (filtersCollapsed = !filtersCollapsed)}
    >
        <i class="fa fa-sliders" aria-hidden="true"></i> Filters
        {#if facetFilterCount > 0}
            <span class="guide-filter-toggle-count">{facetFilterCount}</span>
            <span class="sr-only">active filters</span>
        {/if}
    </button>

    <div
        class="guide-prop-filter"
        class:guide-prop-filter--collapsed={filtersCollapsed}
        id="guide-prop-filter"
        role="group"
        aria-label="Filters"
    >
        {#if collection.groupings.length > 1}
            <label class="guide-prop-control">
                <span class="guide-prop-label">Group by</span>
                <select class="guide-prop-select" bind:value={grouping} aria-label="Group by">
                    {#each collection.groupings as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </label>
        {/if}

        <label class="guide-prop-control">
            <span class="guide-prop-label">Sort by</span>
            <select class="guide-prop-select" bind:value={sort} aria-label="Sort by">
                {#each collection.sorts as option}
                    <option value={option.value}>{option.label}</option>
                {/each}
            </select>
        </label>

        <!-- bind:value rather than value= plus on:change: leave-one-out rebuilds the
             option list under the select, and a one-way value is only re-applied when
             the value itself changes, so the control would fall back to displaying
             "all" while the filter it was still applying stayed active. -->
        {#each facetsWithOptions as facet (facet.id)}
            <label class="guide-prop-control">
                <span class="guide-prop-label sr-only">{facet.label}</span>
                <select
                    class="guide-prop-select"
                    bind:value={facetValues[facet.id]}
                    aria-label={`Filter by ${facet.label}`}
                >
                    <option value="all">{facet.allLabel}</option>
                    {#each facet.resolvedOptions as option (option.value)}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </label>
        {/each}

        {#if hasActiveFilter}
            <button type="button" class="guide-prop-clear" on:click={clearFilters}>
                <i class="fa fa-times" aria-hidden="true"></i> Clear filters
            </button>
        {/if}
    </div>

    {#if activeFilters.length > 0}
        <div class="guide-active-filters" role="group" aria-label="Active filters">
            {#each activeFilters as filter (filter.id)}
                <button
                    type="button"
                    class="guide-active-filter"
                    on:click={() => clearFilter(filter.id)}
                >
                    <span class="guide-active-filter-facet">{filter.label}:</span>
                    <span class="guide-active-filter-value">{filter.display}</span>
                    <i class="fa fa-times" aria-hidden="true"></i>
                    <span class="sr-only">remove this filter</span>
                </button>
            {/each}
        </div>
    {/if}

    {#if showGroupJumpLinks}
        <nav class="guide-group-jump" aria-label="Jump to group">
            {#each groups as group (group.key)}
                {#if group.label}
                    <button
                        type="button"
                        class="guide-group-jump-link"
                        on:click={() => jumpToGroup(group.key)}
                    >
                        {group.label}
                        <span class="guide-group-jump-count">{group.entries.length}</span>
                    </button>
                {/if}
            {/each}
        </nav>
    {/if}

    <p class="guide-result-count" aria-live="polite" bind:this={resultsTop}>
        Showing {filtered.length} of {entries.length}
        {collection.entryNounPlural}
        {#if groupsRepeatEntries}
            <span class="guide-result-note">
                Grouped by {activeGrouping.label.toLowerCase()}, so a {collection.entryNoun} with more
                than one appears in each group: the group counts add up to {groupedEntryCount}.
            </span>
        {/if}
    </p>

    {#if filtered.length === 0}
        <p class="guide-empty">
            No {collection.entryNounPlural} match the current filters.
            {#if hasActiveFilter}
                <button type="button" class="guide-empty-clear" on:click={clearFilters}
                    >Clear filters</button
                >
            {/if}
        </p>
    {:else}
        {#each groups as group (group.key)}
            <section class="guide-group {accentClassFor(group.key, showAccent)}">
                {#if group.label}
                    <h2
                        class="guide-group-heading"
                        tabindex="-1"
                        bind:this={groupHeadings[group.key]}
                    >
                        {#if showAccent}
                            <span class="guide-group-swatch"></span>
                        {/if}
                        {group.label}
                        <span class="guide-group-count">{group.entries.length}</span>
                    </h2>
                {/if}
                <div class="guide-cards" class:guide-cards--list={view === 'list'}>
                    {#each group.entries as entry (entry.slug)}
                        <svelte:component
                            this={collection.card}
                            {entry}
                            {view}
                            searching={normalisedQuery !== ''}
                            groupedBy={activeGrouping.value}
                            on:selectFacet={selectFacetFromCard}
                        />
                    {/each}
                </div>
            </section>
        {/each}
    {/if}
</div>
